import React, { createContext, useState, useContext } from 'react';
import { generateMotivation } from '../services/groqService';

const MotivationContext = createContext();

export const MotivationProvider = ({ children }) => {
  const [motivationalContent, setMotivationalContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState(() => {
    const savedHistory = localStorage.getItem('motivationHistory');
    return savedHistory ? JSON.parse(savedHistory) : [];
  });

  // Generate new motivational content
  const getMotivation = async (prompt) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const content = await generateMotivation(prompt);
      setMotivationalContent(content);
      
      // Save to history
      const newHistoryItem = {
        id: Date.now(),
        prompt,
        content,
        date: new Date().toISOString()
      };
      
      const updatedHistory = [newHistoryItem, ...history].slice(0, 10); // Keep only the 10 most recent
      setHistory(updatedHistory);
      localStorage.setItem('motivationHistory', JSON.stringify(updatedHistory));
      
      return content;
    } catch (err) {
      setError(err.message || 'Failed to generate motivation');
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  // Clear the current motivational content
  const clearMotivation = () => {
    setMotivationalContent('');
  };

  // Clear history
  const clearHistory = () => {
    setHistory([]);
    localStorage.removeItem('motivationHistory');
  };

  return (
    <MotivationContext.Provider 
      value={{ 
        motivationalContent, 
        isLoading, 
        error, 
        history,
        getMotivation,
        clearMotivation,
        clearHistory
      }}
    >
      {children}
    </MotivationContext.Provider>
  );
};

// Custom hook to use the motivation context
export const useMotivation = () => {
  const context = useContext(MotivationContext);
  if (!context) {
    throw new Error('useMotivation must be used within a MotivationProvider');
  }
  return context;
};
